import {
  create,
  type DescMessage,
  type DescMethod,
  type DescMethodUnary,
  type MessageInitShape,
} from '@bufbuild/protobuf';
// oxlint-disable-next-line no-restricted-imports
import { createConnectQueryKey as createConnectQueryKeyNative } from '@connectrpc/connect-query';
import { type SkipToken } from '@tanstack/react-query';
import { transport } from '@/api/transport';

type KeyParamsForMethod<Desc extends DescMethod> = {
  /**
   * Set `serviceName` and `methodName` in the key.
   */
  schema: Desc;
  /**
   * Set `input` in the key:
   * - If a SkipToken is provided, `input` is "skipped".
   * - If an init shape is provided, `input` is set to a message key.
   * - If omitted or undefined, `input` is not set in the key.
   */
  input?: MessageInitShape<Desc['input']> | SkipToken | undefined;
  /**
   * If omit the field with this name from the key for infinite queries.
   */
  pageParamKey?: keyof MessageInitShape<Desc['input']>;
  /**
   * Set `headers` in the key.
   * Note that invalid HTTP header names will raise a TypeError, and that the Set-Cookie header is not supported.
   */
  headers?: HeadersInit;
};

/**
 * Override of createConnectQueryKey to set our default transport and finite cardinality.
 */
export function createConnectQueryKey<I extends DescMessage, O extends DescMessage>({
  input,
  schema,
  ...params
}: KeyParamsForMethod<DescMethodUnary<I, O>>) {
  return createConnectQueryKeyNative({
    schema,
    input: input ?? create(schema.input),
    cardinality: 'finite',
    transport,
    ...params,
  });
}

/**
 * Override of createConnectQueryKey to set our default transport and finite cardinality.
 * It purposefully does not set `input` in the key, since our `useMutation` hook does not currently support it.
 */
export function createConnectMutationKey<I extends DescMessage, O extends DescMessage>({
  input,
  schema,
  ...params
}: Omit<KeyParamsForMethod<DescMethodUnary<I, O>>, 'pageParamKey'>) {
  return createConnectQueryKeyNative({
    schema,
    cardinality: 'finite',
    transport,
    ...params,
  });
}
